// src/services/aeo/timelineFile.ts
//
// Oura reads the Execution Timeline (Jack, 21 Oct 2026). Staff upload the PDF
// the client gets and the data file the Timeline build writes next to it. Oura
// checks the file against the signed quote, shows what will change, and only
// on confirm replaces its plan with the Timeline's: dates as given, nothing
// worked out again, and no email to the client.
//
// The routes are PROPOSED, in the handoff to the backend (Ipul, 21 Oct): until
// they ship this runs on sample data and the screen stays off wherever the
// backend is real (see TIMELINE_READ_ON in src/oura/onboarding/timelineRead.ts).

import axios from "axios";
import { catchErrorLog } from "@util/sentry";
import { getCookie } from "@util/cookie";
import { onboardingSim } from "./onboarding";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;
const AEO_BASE = `${apiURL}/api/v1`;
const useFixtures = import.meta.env.VITE_ENABLE_OURA_TIMELINE_READ_LIVE !== "true";
const enc = encodeURIComponent;

function authHeaders(): Record<string, string> {
    const token = getCookie("authToken");
    return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * One check Oura runs on the file before it shows anything. Only the quote
 * blocks (a Timeline built for another client); everything else may differ
 * from the quote on purpose, so it is a warning staff read and can still
 * confirm (Jack, 21 Oct 2026).
 */
export interface TimelineCheck {
    key: "quote_reference" | "term" | "outlets" | "monday_start" | "dates_in_order" | "answers_total";
    ok: boolean;
    /** True only for the quote reference: a failed one stops the confirm. */
    blocks: boolean;
    /** One plain sentence: what was checked, or how it differs. */
    said: string;
}

/** One fact of the plan, as Oura has it now and as the Timeline has it. */
export interface TimelineChange {
    key: string;
    label: string;
    /** Null: Oura has no value for it today. */
    before: string | null;
    after: string | null;
}

/**
 * One piece of work in one month (a contract line: "Pages linked together"),
 * as Oura plans it now and as the Timeline has it. `inOura` false: the
 * Timeline adds it; `inTimeline` false: the Timeline drops it. The amounts
 * ("10 pages", "5 rivals") are the line's own words, null when it has none.
 */
export interface TimelineWorkItem {
    code: string;
    label: string;
    inOura: boolean;
    inTimeline: boolean;
    before: string | null;
    after: string | null;
}

/** One plan month: its dates, the answers that go live in it, and its work. */
export interface TimelineMonth {
    month: number;
    /** The month's first and last day, from the Timeline. */
    from: string | null;
    to: string | null;
    reportBefore: string | null;
    reportAfter: string | null;
    answersBefore: number | null;
    answersAfter: number | null;
    work: TimelineWorkItem[];
}

export interface TimelinePreview {
    /** Hands the confirm back the file Oura checked, so nothing is read twice. */
    previewId: string;
    /** The Timeline's own version, "v2.0". */
    version: string;
    /** The quote the file names. */
    reference: string;
    checks: TimelineCheck[];
    /** True when a blocking check failed: nothing can be confirmed. */
    blocked: boolean;
    changes: TimelineChange[];
    months: TimelineMonth[];
}

export type TimelinePreviewResult = { ok: true; preview: TimelinePreview } | { ok: false; message: string };
export type TimelineConfirmResult = { ok: true } | { ok: false; message: string };

/** Data files are small; a big one is a wrong file. */
export const TIMELINE_DATA_MAX_BYTES = 1024 * 1024;

/** Upload both files and get back what Oura read and what would change. Saves nothing. */
export async function previewTimeline(businessId: string, pdf: File, data: File): Promise<TimelinePreviewResult> {
    if (useFixtures) return samplePreview(businessId, data);
    const form = new FormData();
    form.append("pdf", pdf);
    form.append("data", data);
    try {
        const res = await axios.post(`${AEO_BASE}/businesses/${enc(businessId)}/onboarding/execution-timeline/preview`, form, {
            headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
            timeout: 120_000,
        });
        return { ok: true, preview: res.data.data as TimelinePreview };
    } catch (err) {
        const message = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
        if (!message) catchErrorLog(`[oura] previewTimeline failed: ${err}`);
        return { ok: false, message: message || "Oura couldn't read the files. Try again in a moment." };
    }
}

/** Replace Oura's plan with the checked Timeline. No email to the client. */
export async function confirmTimeline(businessId: string, previewId: string): Promise<TimelineConfirmResult> {
    if (useFixtures) {
        const p = sample[businessId];
        if (!p || p.previewId !== previewId) return { ok: false, message: "That check has expired. Upload the files again." };
        onboardingSim.timelineUploaded(businessId, p.version);
        return { ok: true };
    }
    try {
        await axios.post(`${AEO_BASE}/businesses/${enc(businessId)}/onboarding/execution-timeline/confirm`, { previewId }, { headers: authHeaders() });
        return { ok: true };
    } catch (err) {
        const message = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
        if (!message) catchErrorLog(`[oura] confirmTimeline failed: ${err}`);
        return { ok: false, message: message || "The plan was not changed. Try again in a moment." };
    }
}

// ---------------------------------------------------------------------------
// Editing what Oura read (Jack, 23 Oct 2026)
// ---------------------------------------------------------------------------
//
// Staff may correct the values the data file prefilled before they use it. An
// edit is written into a copy of the data file and checked again by the same
// preview route, so the checks and the changes always describe what will be
// saved. The PDF is not touched: it still shows the Timeline as it was built.

/** The values staff can correct, as form text. Dates are YYYY-MM-DD. */
export interface TimelineValues {
    start_date: string;
    work_starts: string;
    handover: string;
    term_ends: string;
    ai_check: string;
    /** "12, 31" (a leading Q is fine). */
    signature_questions: string;
    /** One keyword per line. */
    priority_keywords: string;
    months: Array<{ month: number; report_on: string; answers: string }>;
}

export type TimelineData = Record<string, unknown>;

/** The data file as JSON, or null when it is not an object (the check refuses it anyway). */
export async function readTimelineData(file: File): Promise<TimelineData | null> {
    try {
        const parsed: unknown = JSON.parse(await file.text());
        return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? (parsed as TimelineData) : null;
    } catch {
        return null;
    }
}

const text = (v: unknown) => (typeof v === "string" ? v.trim() : typeof v === "number" ? String(v) : "");
const monthsOf = (data: TimelineData) =>
    (Array.isArray(data.months) ? data.months : []).filter((m): m is Record<string, unknown> => !!m && typeof m === "object");

export function timelineValues(data: TimelineData): TimelineValues {
    const list = (v: unknown) => (Array.isArray(v) ? v : []);
    return {
        start_date: text(data.start_date),
        work_starts: text(data.work_starts),
        handover: text(data.handover),
        term_ends: text(data.term_ends),
        ai_check: text(data.ai_check).toLowerCase(),
        signature_questions: list(data.signature_questions).map(text).filter(Boolean).join(", "),
        priority_keywords: list(data.priority_keywords).map(text).filter(Boolean).join("\n"),
        months: monthsOf(data)
            .filter((m) => typeof m.month === "number")
            .map((m) => ({ month: m.month as number, report_on: text(m.report_on), answers: text(m.answers) })),
    };
}

/** How many values differ from the file as built. */
export function timelineEditCount(built: TimelineValues, now: TimelineValues): number {
    const keys = ["start_date", "work_starts", "handover", "term_ends", "ai_check", "signature_questions", "priority_keywords"] as const;
    const top = keys.filter((k) => built[k] !== now[k]).length;
    // A month's report date and answers count as two values.
    const months = now.months.reduce((n, m) => {
        const was = built.months.find((b) => b.month === m.month);
        return n + (!was ? 2 : Number(was.report_on !== m.report_on) + Number(was.answers !== m.answers));
    }, 0);
    return top + months;
}

/**
 * The data file with staff's values written in, or the first value that can't
 * be. Fields staff don't edit (work lines, outlets, the version) pass through.
 */
export function editedTimelineFile(data: TimelineData, values: TimelineValues, name: string): { ok: true; file: File } | { ok: false; message: string } {
    const questions: number[] = [];
    for (const part of values.signature_questions.split(/[\s,]+/).filter(Boolean)) {
        const n = Number(part.replace(/^q/i, ""));
        if (!Number.isInteger(n) || n < 1) return { ok: false, message: `Signature questions: "${part}" is not a question number.` };
        questions.push(n);
    }
    for (const [key, label] of [["start_date", "Start date"], ["work_starts", "Work starts"], ["handover", "Handover"], ["term_ends", "Term ends"]] as const) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(values[key])) return { ok: false, message: `${label} needs a date.` };
    }
    const byMonth = new Map(values.months.map((m) => [m.month, m]));
    const months: unknown[] = [];
    for (const m of Array.isArray(data.months) ? data.months : []) {
        const row = m && typeof m === "object" ? (m as Record<string, unknown>) : null;
        const edit = row && typeof row.month === "number" ? byMonth.get(row.month) : undefined;
        if (!row || !edit) { months.push(m); continue; }
        if (!/^\d{4}-\d{2}-\d{2}$/.test(edit.report_on)) return { ok: false, message: `Month ${edit.month}: the report needs a date.` };
        const answers = Number(edit.answers);
        if (edit.answers.trim() === "" || !Number.isInteger(answers) || answers < 0) return { ok: false, message: `Month ${edit.month}: answers must be a whole number.` };
        months.push({ ...row, report_on: edit.report_on, answers });
    }
    const next: TimelineData = {
        ...data,
        start_date: values.start_date,
        work_starts: values.work_starts,
        handover: values.handover,
        term_ends: values.term_ends,
        ai_check: values.ai_check,
        signature_questions: questions,
        priority_keywords: values.priority_keywords.split("\n").map((k) => k.trim()).filter(Boolean),
        months,
    };
    return { ok: true, file: new File([JSON.stringify(next, null, 2)], name, { type: "application/json" }) };
}

// ---------------------------------------------------------------------------
// Sample: one realistic Timeline against the sample client's quote
// ---------------------------------------------------------------------------

const SAMPLE_REFERENCE = "OT-Q-2026-0142";

const SAMPLE_MONTHS: Array<[number, string, string, string, string, number, number]> = [
    [1, "26 Oct", "25 Nov", "18 Nov", "25 Nov", 3, 10],
    [2, "26 Nov", "25 Dec", "17 Dec", "24 Dec", 3, 2],
    [3, "26 Dec", "25 Jan", "18 Jan", "25 Jan", 3, 2],
    [4, "26 Jan", "25 Feb", "18 Feb", "25 Feb", 3, 2],
    [5, "26 Feb", "25 Mar", "18 Mar", "25 Mar", 3, 2],
    [6, "26 Mar", "25 Apr", "16 Apr", "23 Apr", 3, 2],
];

const FOUNDATION: Array<[string, string, string | null, string | null]> = [
    ["B1", "Pages written and published", "3 pages", "10 pages, 2 of them Signature"],
    ["S1", "Existing pages labelled", "10 pages", "10 pages"],
    ["S3", "Technical crawl, broken pages and dead links fixed", null, null],
    ["S4", "Your name, address and contact corrected across the web", null, null],
    ["S5", "Search Console set up, baseline recorded", null, null],
    ["S6", "Pages linked together", null, null],
    ["S7", "Pages load fast", "10 pages", "10 pages"],
    ["A1", "Your 50 questions locked and scored on day one", null, null],
    ["A2", "Business and page facts tagged for AI", null, null],
    ["A3", "Let AI read your site", null, null],
    ["A4", "Handover Scorecard and call", null, null],
];

/** The sample Timeline's work: 10 pages in month 1 then 2 a month, AI check every 3 months, competitor watch added. */
function sampleWork(month: number): TimelineWorkItem[] {
    const same = (code: string, label: string, amount: string | null = null): TimelineWorkItem =>
        ({ code, label, inOura: true, inTimeline: true, before: amount, after: amount });
    if (month === 1) {
        return [
            ...FOUNDATION.map(([code, label, before, after]) => ({ code, label, inOura: true, inTimeline: true, before, after })),
            { code: "G7", label: "AI visibility check", inOura: true, inTimeline: false, before: "each question asked 3 times", after: null },
        ];
    }
    const quarterly = month % 3 === 0;
    return [
        { code: "G1", label: "New pages this month", inOura: true, inTimeline: true, before: "3 pages", after: "2 pages" },
        same("G2", "Site health sweep"),
        same("G3", "Your scoreboard"),
        { code: "G4", label: "Competitor watch", inOura: false, inTimeline: true, before: null, after: "5 rivals" },
        same("G5", "1 small change", "1"),
        same("G6", "Your monthly snapshot"),
        { code: "G7", label: "AI visibility check", inOura: true, inTimeline: quarterly, before: "each question asked 3 times", after: quarterly ? "each question asked 3 times" : null },
        ...(month === 6 ? [same("G8", "Term review", "60 minutes")] : []),
    ];
}
const sample: Record<string, TimelinePreview> = {};

const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** "14 Sep" or "Mon 14 Sep" from YYYY-MM-DD, as the backend words them; null when it isn't a date. */
function sampleDay(iso: string, weekday = false): string | null {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!m) return null;
    const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
    const text = `${d.getUTCDate()} ${MON[d.getUTCMonth()]}`;
    return weekday ? `${WEEKDAY[d.getUTCDay()]} ${text}` : text;
}

const CADENCE: Record<string, string> = { monthly: "Every month", quarterly: "Every 3 months", yearly: "Every 12 months" };

async function samplePreview(businessId: string, data: File): Promise<TimelinePreviewResult> {
    await new Promise((r) => window.setTimeout(r, 700));
    const file = await readTimelineData(data);
    if (!file) return { ok: false, message: "That isn't a Timeline data file. Use the .json file the Timeline build saved next to the PDF." };
    const reference = typeof file.reference === "string" && file.reference.trim() ? file.reference.trim() : SAMPLE_REFERENCE;
    const version = typeof file.version === "string" && file.version.trim() ? file.version.trim() : "v2.0";
    const sameQuote = reference === SAMPLE_REFERENCE;
    // A data file that carries the plan's values drives "From the Timeline", so an edit shows
    // through the sample as it does through the backend; a bare one falls back to the sample.
    const v = timelineValues(file);
    const after = (iso: string, fallback: string, weekday = false) => sampleDay(iso, weekday) ?? fallback;
    const fileMonths = new Map(v.months.map((m) => [m.month, m]));
    const months = SAMPLE_MONTHS.map(([month, from, to, reportBefore, reportAfter, answersBefore, answersAfter]) => {
        const m = fileMonths.get(month);
        const answers = m && m.answers !== "" && Number.isInteger(Number(m.answers)) ? Number(m.answers) : answersAfter;
        return { month, from, to, reportBefore, reportAfter: m ? after(m.report_on, reportAfter) : reportAfter, answersBefore, answersAfter: answers, work: sampleWork(month) };
    });
    const total = months.reduce((n, m) => n + (m.answersAfter ?? 0), 0);
    const ordered = !(v.work_starts && v.start_date && v.work_starts < v.start_date)
        && !(v.handover && v.work_starts && v.handover < v.work_starts)
        && !(v.term_ends && v.handover && v.term_ends < v.handover);
    const monday = !v.work_starts || sampleDay(v.work_starts, true)?.startsWith("Mon ") !== false;
    const checks: TimelineCheck[] = [
        { key: "quote_reference", ok: sameQuote, blocks: true, said: sameQuote ? `Same quote, ${SAMPLE_REFERENCE}` : `The file is for quote ${reference}, but this client signed ${SAMPLE_REFERENCE}. Rebuild the Timeline from the right quote.` },
        { key: "term", ok: true, blocks: false, said: "Same term, 6 months" },
        { key: "outlets", ok: true, blocks: false, said: "Same outlets, 1" },
        { key: "monday_start", ok: monday, blocks: false, said: monday ? `Work starts on a Monday, ${after(v.work_starts, "26 Oct")}` : `Work starts on ${after(v.work_starts, "26 Oct", true)}, not a Monday` },
        { key: "dates_in_order", ok: ordered, blocks: false, said: ordered ? "Every date is in order" : "Work starts, handover or term end is before the date it follows" },
        { key: "answers_total", ok: total === 18, blocks: false, said: total === 18 ? "18 answers, as the quote sold" : `${total} answers. The quote sold 18` },
    ];
    const keywords = v.priority_keywords ? v.priority_keywords.split("\n").filter(Boolean).length : 5;
    const preview: TimelinePreview = {
        previewId: `sample-${Date.now()}`,
        version,
        reference,
        checks,
        blocked: checks.some((c) => !c.ok && c.blocks),
        changes: [
            { key: "start", label: "Start date", before: "12 Oct", after: after(v.start_date, "19 Oct") },
            { key: "work", label: "Work starts", before: "Mon 19 Oct", after: after(v.work_starts, "Mon 26 Oct", true) },
            { key: "handover", label: "Handover", before: "Fri 13 Nov", after: after(v.handover, "Fri 20 Nov", true) },
            { key: "end", label: "Term ends", before: "18 Apr", after: after(v.term_ends, "25 Apr") },
            { key: "check", label: "AI check", before: "Every month", after: CADENCE[v.ai_check] ?? "Every 3 months" },
            { key: "signature", label: "Signature questions", before: "Q12", after: v.signature_questions ? v.signature_questions.split(/[\s,]+/).filter(Boolean).map((q) => `Q${q.replace(/^q/i, "")}`).join(", ") : "Q12, Q31" },
            { key: "keywords", label: "Priority keywords", before: "5", after: String(keywords) },
        ],
        months,
    };
    sample[businessId] = preview;
    return { ok: true, preview };
}
